import Keycloak from 'keycloak-js/lib/keycloak.js';

let installed = false;

function defaultEmptyVueKeycloakInstance() {
  return {
    ready: false,
    authenticated: false,
    userName: null,
    fullName: null,
    token: null,
    tokenParsed: null,
    logoutFn: null,
    loginFn: null,
    login: null,
    createLoginUrl: null,
    createLogoutUrl: null,
    createRegisterUrl: null,
    register: null,
    accountManagement: null,
    createAccountUrl: null,
    loadUserProfile: null,
    subject: null,
    idToken: null,
    idTokenParsed: null,
    realmAccess: null,
    resourceAccess: null,
    refreshToken: null,
    refreshTokenParsed: null,
    timeSkew: null,
    responseMode: null,
    responseType: null,
    hasRealmRole: null,
    hasResourceRole: null,
    keycloak: null,
  };
}

export default {
  install(Vue, params = {}) {
    if (installed) return;
    installed = true;

    const defaultParams = {
      init: { onLoad: 'login-required' },
    };
    const options = Object.assign({}, defaultParams, params);

    // Create a reactive watchable wrapper object using Vue.observable()
    const watch = Vue.observable(defaultEmptyVueKeycloakInstance());

    Object.defineProperty(Vue.prototype, '$keycloak', {
      get() {
        return watch;
      }
    });

    const keycloak = new Keycloak(options.config);
    const updateInterval = options.updateInterval ?? 10000;

    function updateWatchVariables(isAuthenticated = false) {
      watch.authenticated = isAuthenticated;
      watch.loginFn = keycloak.login.bind(keycloak);
      watch.login = keycloak.login.bind(keycloak);
      watch.createLoginUrl = keycloak.createLoginUrl.bind(keycloak);
      watch.createLogoutUrl = keycloak.createLogoutUrl.bind(keycloak);
      watch.createRegisterUrl = keycloak.createRegisterUrl.bind(keycloak);
      watch.register = keycloak.register.bind(keycloak);
      watch.keycloak = keycloak;
      if (isAuthenticated) {
        watch.accountManagement = keycloak.accountManagement.bind(keycloak);
        watch.createAccountUrl = keycloak.createAccountUrl.bind(keycloak);
        watch.hasRealmRole = keycloak.hasRealmRole.bind(keycloak);
        watch.hasResourceRole = keycloak.hasResourceRole.bind(keycloak);
        watch.loadUserProfile = keycloak.loadUserProfile.bind(keycloak);
        watch.token = keycloak.token;
        watch.subject = keycloak.subject;
        watch.idToken = keycloak.idToken;
        watch.idTokenParsed = keycloak.idTokenParsed;
        watch.realmAccess = keycloak.realmAccess;
        watch.resourceAccess = keycloak.resourceAccess;
        watch.refreshToken = keycloak.refreshToken;
        watch.refreshTokenParsed = keycloak.refreshTokenParsed;
        watch.timeSkew = keycloak.timeSkew;
        watch.responseMode = keycloak.responseMode;
        watch.responseType = keycloak.responseType;
        watch.tokenParsed = keycloak.tokenParsed;
        watch.userName = keycloak.tokenParsed ? keycloak.tokenParsed['preferred_username'] : null;
        watch.fullName = keycloak.tokenParsed ? keycloak.tokenParsed['name'] : null;
      }
    }

    keycloak.onReady = function (authenticated) {
      updateWatchVariables(authenticated);
      watch.ready = true;
      if (typeof options.onReady === 'function') {
        options.onReady(keycloak, watch);
      }
    };

    keycloak.onAuthSuccess = function () {
      // Check token validity every 10 seconds (or custom updateInterval) and, if necessary, update the token.
      // Refresh token if it's valid for less than 60 seconds
      const updateTokenInterval = setInterval(() => {
        keycloak.updateToken(60).catch(() => {
          keycloak.clearToken();
        });
      }, updateInterval);

      watch.logoutFn = () => {
        clearInterval(updateTokenInterval);
        return keycloak.logout(options.logout);
      };
    };

    keycloak.onAuthRefreshSuccess = function () {
      updateWatchVariables(true);
      if (typeof options.onAuthRefreshSuccess === 'function') {
        options.onAuthRefreshSuccess(keycloak);
      }
    };

    keycloak.onAuthRefreshError = function () {
      updateWatchVariables(false);
      if (typeof options.onAuthRefreshError === 'function') {
        options.onAuthRefreshError(keycloak);
      }
    };

    keycloak.onAuthLogout = function () {
      updateWatchVariables(false);
      if (typeof options.onAuthLogout === 'function') {
        options.onAuthLogout(keycloak);
      }
    };

    keycloak
      .init(options.init)
      .then((authenticated) => {
        updateWatchVariables(authenticated);
        if (typeof options.onInitSuccess === 'function') {
          options.onInitSuccess(authenticated);
        }
      })
      .catch((err) => {
        updateWatchVariables(false);
        const error = Error('Failure during initialization of keycloak-js adapter');
        if (typeof options.onInitError === 'function') {
          options.onInitError(error, err);
        } else {
          console.error(error, err);
        }
      });
  }
};
